import { Component, ViewChild } from '@angular/core';
import { ModalService } from '../../../services/modal/modal.service';
import { BasicTextInputComponent } from '../../form/base-components/base-text-input/basic-text-input.component';
import { SelectBasicComponent } from '../../form/base-components/select-basic/select-basic.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Controller } from '../../../../../coverpath-client/public/js/Controller';
import { BaseModalComponent } from '../base-modal.component';
import { ModalType } from '../../../../js/interfaces/CoverpathTypes';
import { PaymentInformation } from '../../../../classes/PaymentInformation';
import type { Policy } from '../../../../../common/js/customer/Policy';
import { Product } from '../../../../../common/js/static/Product';
import { Utils } from '../../../../../common/js/Utils';
import { ToastService } from '../../overlays/toaster/toast-service.service';
import { IsLoadingType } from '../../../interfaces/account-center/IsLoadingDataInterface';
import { ValidationService } from './../../../services/validation/validation.service';
import { GiactUtils } from '../../../../../common/js/GiactUtils';
import { ValidationErrorMessages } from '../../../classes/UnsupportedUseCases';
import { PaymentService } from '../../../../../common/widgets/payment-update-widget/js/common/service/payment.service';
@Component({
  moduleId: module.id,
  templateUrl: './payment-if-missing-container.component.html',
})
export class PaymentIfMissingContainerComponent extends BaseModalComponent {
  @ViewChild('bankName') bankNameInput: BasicTextInputComponent;
  @ViewChild('ABA') abaInput: BasicTextInputComponent;
  @ViewChild('Account') accountInput: BasicTextInputComponent;
  @ViewChild('accountType') accountTypeInput: SelectBasicComponent;
  controller: Controller;
  accountCenterController;
  submitDisabled: boolean = true;
  instanceName: string;
  formattedActiveFace: string;
  formattedFace: string;
  paymentInformation: PaymentInformation;
  faceOverTlicFace: boolean;
  tlicCap: number;
  formattedTlicCap: string;
  formattedPremiumAmount: string;
  policyDescription: string;
  secondaryPolicyDescription: string;
  secondaryFormattedFace: string;
  paymentTerm: string;
  paymentTermAbbrv: string;
  accountEnding: string;
  formattedTlicAmount: string;
  existingAccountType: string;
  isJointApp: boolean;
  isMMD: boolean = false;
  paymentFrequency: string;
  paymentInfoError: string;
  isAccountRoutingInfoSame: boolean = false;

  constructor(
    private amorphicService: AmorphicService,
    modalService: ModalService,
    private accountCenterService: AccountCenterService,
    private toastService: ToastService,
    private paymentService: PaymentService
  ) {
    super(modalService);
    this.modalService = modalService;
    this.accountCenterService = accountCenterService;
    this.controller = this.amorphicService.controller as Controller;
    this.accountCenterController = this.controller.accountCenterController;
    this.paymentInformation = this.accountCenterController.paymentInformation;
    this.accountCenterController.paymentInformation.isAutoPay =
      this.controller.customer.application.primaryPolicy.isAutoPay;
    this.paymentService = paymentService;
  }

  getPolicyDescription(policy: Policy) {
    return policy.productType === Product.productTypes.Term.code
      ? policy.termWithPendingAmendment() + ' Year Convertible Term '
      : 'Whole Life ' + policy.term;
  }

  ngOnInit() {
    this.instanceName = this.data.modalData.instance;
    const policy = this.controller.customer.application.primaryPolicy;
    this.isMMD = !!policy.isMMDirectPolicy();
    this.formattedFace = Utils.formatIntCurrency(policy.faceAmountWithPendingAmendment().toString());
    this.tlicCap = policy.tLICFaceAmountCap;
    this.policyDescription = this.getPolicyDescription(policy);
    this.paymentTerm = policy.paymentFrequencyWithPendingAmendment().toLowerCase() === 'annual' ? "year's" : "month's";
    this.paymentTermAbbrv = policy.paymentFrequencyWithPendingAmendment().toLowerCase() === 'annual' ? 'yr' : 'mo';
    this.paymentFrequency = policy.paymentFrequencyWithPendingAmendment().toLowerCase();
    this.paymentService.resetGiactResponse();
    let secondaryPolicy;
    if (policy.isPartOfJointApp()) {
      secondaryPolicy = this.controller.customer.application.secondaryPolicy;
      this.isJointApp = true;
      this.secondaryPolicyDescription = this.getPolicyDescription(secondaryPolicy);
      this.secondaryFormattedFace = Utils.formatIntCurrency(secondaryPolicy.faceAmount.toString());
    }

    const accountTypes = { '1': 'Savings', '2': 'Checking' };
    if (policy.paymentInformationGathered) {
      const account = policy.paymentAccount;
      this.accountEnding = account.substring(account.length - 4, account.length);
      this.existingAccountType = accountTypes[policy.paymentAccountType];
    } else {
      this.paymentInformation.paymentAccountType = '2';
    }

    if (this.instanceName === 'payment-tlic' || this.instanceName === 'information-tlic') {
      this.formattedTlicAmount = Utils.formatCurrency(policy.tLICPremiumBreakdown.getTotal(), 2);
      this.formattedTlicCap = Utils.formatCurrency(this.tlicCap, 2);
      if (policy.faceAmount > this.tlicCap) {
        this.faceOverTlicFace = true;
        this.formattedActiveFace = this.formattedTlicCap;
      } else {
        this.faceOverTlicFace = false;
        this.formattedActiveFace = this.formattedFace;
      }
    } else {
      let premium = policy.premiumBreakdown.getTotalWithPendingAmendment();
      if (secondaryPolicy) {
        premium += secondaryPolicy.premiumBreakdown.getTotalWithPendingAmendment();
      }
      this.formattedPremiumAmount = Utils.formatCurrency(premium, 2);
      this.formattedActiveFace = this.formattedFace;
    }

    if (this.instanceName !== 'information-tlic' && this.instanceName !== 'confirm-tlic') {
      setTimeout(async () => {
        await this.updateValidation();
      });
    }
  }

  async acceptOffer() {
    if (await this.updateValidation()) {
      return;
    }
    await this.controller.accountCenterController.applyUpdatedPaymentInformationToPolicy(
      this.accountCenterService.primaryPolicy.policyNumber
    );
    this.accountCenterService.acceptFromService();
    this.modalService.deactivate();
  }

  async acceptAndSign() {
    if (await this.updateValidation()) {
      return;
    }
    await this.controller.accountCenterController.applyUpdatedPaymentInformationToPolicy(
      this.accountCenterService.primaryPolicy.policyNumber
    );
    this.accountCenterService.isLoadingSubject.next({
      isLoading: true,
      type: IsLoadingType.DURING,
      action: this.accountCenterService.latestActionString,
    });
    await this.accountCenterService.esignFromService();
    this.modalService.deactivate();
  }

  async applyUpdatedPaymentInfoThenOpenClientEsignModal() {
    if (await this.updateValidation()) {
      return;
    }
    await this.controller.accountCenterController.applyUpdatedPaymentInformationToPolicy(
      this.accountCenterService.primaryPolicy.policyNumber
    );
    this.modalService.deactivate();
    this.modalService.activate({
      type: ModalType.clientEsign,
    });
  }

  async applyConversionUpdatedPaymentInfoThenOpenClientEsignModal() {
    if (await this.updateValidation()) {
      return;
    }
    await this.controller.accountCenterController.applyUpdatedPaymentInformationToPolicy(
      this.accountCenterService.primaryPolicy.policyNumber
    );
    await this.controller.accountCenterController.checkRequirement(
      this.accountCenterService.primaryPolicy,
      'PaymentDetailsUpdated',
      ''
    );
    this.modalService.deactivate();
    this.modalService.activate({
      type: ModalType.clientConversionEsign,
    });
  }

  async updateValidation() {
    if (this.bankNameInput.val && this.abaInput.val && this.accountInput.val && this.accountTypeInput.selectedItem) {
      this.bankNameInput.triggerBase();
      this.abaInput.triggerBase();
      this.accountInput.triggerBase();
      const paymentInfo = {
        paymentABA: this.abaInput.value,
        paymentAccount: this.accountInput.value,
      };
      this.isAccountRoutingInfoSame = ValidationService.isAccountRoutingInfoSame(paymentInfo);
      if (
        !this.bankNameInput.isError &&
        !this.abaInput.isError &&
        !this.accountInput.isError &&
        !this.accountTypeInput.isError &&
        !this.isAccountRoutingInfoSame
      ) {
        const paymentInfoCombo: string = `${this.accountInput.val}${this.abaInput.val}`;
        const oldPaymentInfoCombo: string = this.accountCenterController.lastWebDebitAttemptCombination;
        if (ValidationService.verifyIsNewPaymentCombination(paymentInfoCombo, oldPaymentInfoCombo)) {
          const giactReponse = await ValidationService.verifyWebDebit(paymentInfo);
          this.accountCenterController.isGiactWebDebitValid = giactReponse.status === 'pass';
          this.accountCenterController.giactWebDebitResponseCode = giactReponse.normalizedResponseCode;
          this.accountCenterController.lastWebDebitAttemptCombination = paymentInfoCombo;
          this.isInvalidPaymentInfo();
          if (this.accountCenterController.isGiactWebDebitValid) {
            this.submitDisabled = false;
            return false;
          }
        } else if (this.accountCenterController.isGiactWebDebitValid) {
          //If account type and bank name is changed
          this.submitDisabled = false;
          return false;
        }
      }
    }

    this.submitDisabled = true;
    return true;
  }

  isInvalidPaymentInfo() {
    this.paymentInfoError = this.getErrorMessage();
    return (
      this.isAccountRoutingInfoSame ||
      GiactUtils.isInvalidPayment(
        this.accountCenterController.isGiactWebDebitValid,
        this.accountCenterController.giactWebDebitResponseCode,
        this.controller.mmGiactWebDebitVerificationServiceEnabled
      )
    );
  }

  getErrorMessage() {
    return this.isAccountRoutingInfoSame
      ? ValidationErrorMessages.samePaymentInfoError
      : GiactUtils.getNormalizedGiactMessage(
          this.controller.mmGiactWebDebitVerificationServiceEnabled,
          this.accountCenterController.giactWebDebitResponseCode
        );
  }

  cancel() {
    this.modalService.deactivate();
  }

  confirm() {}

  proceedToPayment() {
    this.modalService.deactivate();

    const paymentModalMode = this.accountCenterService.primaryPolicy.paymentInformationGathered
      ? 'information-tlic'
      : 'payment-tlic';

    this.modalService.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: paymentModalMode },
    });
  }

  declineTlicOffer() {
    this.modalService.deactivate();
    this.modalService.activate({
      type: ModalType.declineTLICConfirm,
    });
  }

  async acceptTlicOffer() {
    await this.accountCenterService.esignFromService();
    await this.accountCenterService.forceWorkflowStateNotification();

    this.modalService.deactivate();
    this.toastService.showToast('You accepted temporary coverage.');
  }

  async applyUpdatedPaymentInformationAndAcceptTlicOffer() {
    if (await this.updateValidation()) {
      return;
    }
    await this.controller.accountCenterController.applyUpdatedPaymentInformationToPolicy(
      this.accountCenterService.primaryPolicy.policyNumber
    );

    this.acceptTlicOffer();
  }
}
